'use client';
import { ClerkLoaded } from '@clerk/nextjs';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { useBooqable } from '@dolcevia/lib/hooks/useBooqable';
import { cn } from '@dolcevia/lib/utils';
import { Icons } from '@dolcevia/ui/components/icons';
import { Button } from '@dolcevia/ui/components/ui/button';
import { HamburgerMenuIcon } from '@radix-ui/react-icons';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import * as React from 'react';
import { type Dispatch, type SetStateAction, useRef, useState } from 'react';
import { useMediaQuery } from './ui/stepper/use-media-query';
import { UserNav } from './ui/user-nav';

let isDatadogInitialized = false;
export const useDataDogRum = () => {
  React.useEffect(() => {
    if (isDatadogInitialized) return;
    datadogRum.init({
      applicationId: '5050e5d4-c715-4791-8023-da556624d08d',
      clientToken: 'pub5c62388e4784bc17e5a372dd384496f6',
      site: 'datadoghq.eu',
      service: process.env.NEXT_PUBLIC_DD_WEBSITE ?? 'dolcevia',
      env: process.env.NODE_ENV,
      version: process.env.NEXT_PUBLIC_COMMIT_SHA,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      plugins: [reactPlugin({ router: false })],
    });
    isDatadogInitialized = true;
  }, []);
};
interface MainMenuProps {
  className?: string;
}

export function MainMenu({ className }: MainMenuProps) {
  const pathname = usePathname();
  const [position, setPosition] = useState<Position>({
    left: 0,
    width: 0,
    opacity: 0,
  });
  const [positionActive, setPositionActive] = useState<Position>({
    left: 0,
    width: 0,
    opacity: 0,
  });

  useBooqable();
  useDataDogRum();

  const isMobile = useMediaQuery(`(max-width: '768px'})`);
  const t = useTranslations('Menu');

  const routes = [
    { name: t('Découvrir'), path: '/', basePath: 'https://www.dolcevia.eu' },
    { name: t('Nos destinations'), path: '/destinations' },
    {
      name: t('Le Planner'),
      path: '/',
      basePath: 'https://planner.dolcevia.eu',
    },
    { name: t('Location'), path: '/locations' },
    { name: t('Conseils & Inspirations'), path: '/posts' },
  ];

  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <div className="w-full bg-hello-yellow-100 font-semibold text-midnight-100 text-base text-center underline">
        <Link href="/locations">
          {t(
            'Bénéficiez de notre assurance intempérie exclusive à Paris ! Vous êtes 100% remboursé en cas de pluie',
          )}
        </Link>
        <br />
        <Link href="/locations">
          {t('Plus de raison de se priver de louer 😉')}
        </Link>
      </div>
      <nav
        className={cn('w-full container top-0 sticky z-50 bg-white', className)}
      >
        <div className="z-50 relative items-center max-w-screen-xl md:flex">
          <div className="flex items-center justify-between py-3 md:py-5 md:block">
            <Link href="https://www.dolcevia.eu/" aria-label="Home">
              <Icons.logo />
            </Link>
            <div className="md:hidden">
              <button
                type="button"
                className="text-gray-700 outline-none p-2 rounded-md focus:border-gray-400 focus:border"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-label="Menu"
              >
                <HamburgerMenuIcon />
              </button>
            </div>
          </div>
          <div
            className={`flex-1 justify-self-center pb-3 mt-8 md:block md:pb-0 md:mt-0 ${
              isOpen ? 'block' : 'hidden'
            }`}
          >
            <ul
              className="justify-center items-center space-y-8 md:flex gap-3 md:space-y-0 relative"
              onMouseLeave={() =>
                setPosition((prev) => ({
                  left: prev.left,
                  width: prev.width,
                  opacity: 0,
                }))
              }
            >
              {routes.map((route) => (
                <MenuLink
                  key={route.name}
                  route={route}
                  setPosition={setPosition}
                  setPositionActive={setPositionActive}
                  setIsOpen={setIsOpen}
                />
              ))}
              <li className="md:hidden flex gap-4">
                <Button asChild variant="secondary">
                  <Link
                    href="/contact"
                    prefetch={
                      process.env.NEXT_PUBLIC_DD_WEBSITE ===
                      'dolcevia---website'
                    }
                  >
                    {t('Nous contacter')}
                  </Link>
                </Button>
                <ClerkLoaded>
                  <UserNav />
                </ClerkLoaded>
              </li>
              <Cursor position={position} />
              <Cursor position={positionActive} />
            </ul>
          </div>
          {!isMobile && (
            <div className="hidden items-center md:flex gap-4">
              <Button asChild variant="secondary">
                <Link href="/contact">{t('Nous contacter')}</Link>
              </Button>
              <div
                className={`booqable-cart-button bg-hello-yellow-100 p-2 rounded-full ${pathname.includes('book') ? 'hidden' : ''}`}
                aria-label="cart button"
              />
              <ClerkLoaded>
                <UserNav />
              </ClerkLoaded>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}

const MenuLink = ({
  route,
  setPosition,
  setPositionActive,
  setIsOpen,
}: {
  route: { name: string; path: string; basePath?: string };
  setPosition: Dispatch<SetStateAction<Position>>;
  setPositionActive: Dispatch<SetStateAction<Position>>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const ref = useRef<null | HTMLLIElement>(null);
  const pathname = usePathname();
  function isPathActive(
    route: { name: string; path: string; basePath?: string },
    pathname: string,
  ): boolean {
    if (route.path === '/' && route.basePath === 'https://www.dolcevia.eu') {
      return (
        pathname === '/' &&
        !process.env.NEXT_PUBLIC_DD_WEBSITE?.includes('planner')
      );
    }
    if (
      route.path === '/' &&
      route.basePath === 'https://planner.dolcevia.eu'
    ) {
      return process.env.NEXT_PUBLIC_DD_WEBSITE?.includes('planner') ?? false;
    }
    return pathname.startsWith(route.path);
  }
  const isActive = isPathActive(route, pathname);
  React.useEffect(() => {
    if (!ref?.current || !isActive) return;

    const { width } = ref.current.getBoundingClientRect();
    setPositionActive({
      left: ref.current.offsetLeft,
      width: width,
      opacity: 1,
    });
  }, [isActive, setPositionActive]);
  return (
    <li
      ref={ref}
      onMouseEnter={() => {
        if (!ref?.current) return;

        const { width } = ref.current.getBoundingClientRect();
        setPosition({
          left: ref.current.offsetLeft,
          width: width,
          opacity: 1,
        });
      }}
      onClick={() => setIsOpen(false)}
      className={cn('relative z-10 px-3')}
    >
      <Link
        className={cn(
          isActive && 'underline',
          'font-bold transition-colors text-midnight-100 text-base inline-flex',
        )}
        href={`${route.basePath ?? ''}${route.path}`}
        prefetch={process.env.NEXT_PUBLIC_DD_WEBSITE === 'dolcevia---website'}
      >
        {route.name}
      </Link>
    </li>
  );
};
const Cursor = ({ position }: { position: Position }) => {
  return (
    <motion.li
      animate={position}
      className="absolute z-0 h-2 bg-hello-yellow-100 -skew-x-20"
    />
  );
};
type Position = {
  left: number;
  width: number;
  opacity: number;
};
